import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { count } from "console";

interface AuthContextType {
  user: User | null;
  profile: any | null;
  loading: boolean;
  signInWithOTP: (email: string) => Promise<void>;
  signInWithPhoneOTP: (phone: string) => Promise<void>;
  verifyOTP: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  console.log("Auth state:", { user, loading });
  useEffect(() => {
    // Check active session
    supabase.auth
      .getSession()
      .then(({ data: { session } }: { data: { session: Session | null } }) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          loadProfile(session.user.id);
        }
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, session: Session | null) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          loadProfile(session.user.id);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId: string) => {
    const { data } = await supabase
      .from("user_profiles")
      .select("*")
      .eq("id", userId)
      .single();

    setProfile(data);
  };

  const signInWithOTP = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;
  };

  const signInWithPhoneOTP = async (phone: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      phone,
    });
    if (error) throw error;
  };

  const verifyOTP = async (emailOrPhone: string, token: string) => {
    // 1. Verify OTP with Supabase Auth
    const { error: otpError } = await supabase.auth.verifyOtp({
      email: emailOrPhone, // works for email OTP
      token,
      type: "email", // or 'sms' if you’re using phone OTP
    });

    if (otpError) throw otpError;

    // 2. Check if user exists in user_profiles table
    const { count, error: profileError } = await supabase
      .from("user_profiles")
      .select("*", { count: "exact", head: true }) // head:true avoids fetching rows, just count
      .or(`email.eq.${emailOrPhone},phone.eq.${emailOrPhone}`); // check both email OR phone

    if (profileError) throw profileError;

    // 3. Decide what to do
    if (count === 0) {
      // User not found → redirect to register page
      window.location.href = "/register";
    } else {
      // User exists → continue to app
      window.location.href = "/dashboard";
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        signInWithOTP,
        signInWithPhoneOTP,
        verifyOTP,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
